import { pubSub } from "../ably";
import { CHANNELS, MESSAGE_NAMES } from "../channel/channels.js";
import { suppressSettingEcho } from "./useCmeSessionSync.js";
import { isInteractiveQuiz } from "../utils/pollTypes.js";
import { queryClient } from "../api/queryClient.js";
import { buildSessionCacheKey } from "../realtime/channelRegistry.js";

const activeIdsMemory = { qna: new Map(), polls: new Map() };

function loadActiveIds(sessionId, kind) {
  const map = activeIdsMemory[kind] || (activeIdsMemory[kind] = new Map());
  if (!map.has(sessionId)) map.set(sessionId, new Set());
  return new Set(map.get(sessionId));
}

function saveActiveIds(sessionId, kind, set) {
  const map = activeIdsMemory[kind] || (activeIdsMemory[kind] = new Map());
  map.set(sessionId, new Set(set));
}

function markIdActive(sessionId, kind, id) {
  const s = loadActiveIds(sessionId, kind);
  s.add(String(id));
  saveActiveIds(sessionId, kind, s);
}

function markIdInactive(sessionId, kind, id) {
  const s = loadActiveIds(sessionId, kind);
  s.delete(String(id));
  saveActiveIds(sessionId, kind, s);
}

const RECENT_LOCAL_TOGGLE_GRACE_MS = 20000;
const recentLocalToggles = { qna: new Map(), polls: new Map() };

function markRecentLocalToggle(sessionId, kind, ids) {
  const map = recentLocalToggles[kind] || (recentLocalToggles[kind] = new Map());
  const key = Number(sessionId);
  const perSession = map.get(key) || new Map();
  const now = Date.now();
  for (const id of ids) {
    if (id == null) continue;
    perSession.set(String(id), now);
  }
  map.set(key, perSession);
}

function hasRecentLocalToggle(sessionId, kind, ids) {
  const map = recentLocalToggles[kind] || (recentLocalToggles[kind] = new Map());
  const perSession = map.get(Number(sessionId));
  if (!perSession || !perSession.size) return false;
  const now = Date.now();
  for (const id of ids) {
    if (id == null) continue;
    const at = perSession.get(String(id));
    if (at != null && now - at < RECENT_LOCAL_TOGGLE_GRACE_MS) return true;
  }
  return false;
}

function buildQnaPayloadItem(q, isApproved) {
  return {
    ...q,
    id: q.id,
    question: typeof q.question === "string" ? q.question : "",
    isApproved: isApproved === true,
  };
}
function buildPollPayloadItem(poll, isApproved) {
  const numericId = poll.id;
  const category = typeof poll.category === "string" ? poll.category.trim().toLowerCase() : (poll.type === "interactive-quiz" || poll.type === "interactive" ? "interactive" : "poll");
  const data = poll.data && typeof poll.data === "object" && !Array.isArray(poll.data) ? poll.data : {};
  const normalizedData = {};
  Object.keys(data).forEach((k) => {
    const v = data[k];
    if (v && typeof v === "object" && /^question\d+$/i.test(k)) {
      normalizedData[k] = {
        ...v,
        questionID: typeof v.questionID === "string" ? v.questionID : typeof v.qid === "string" ? v.qid : v.questionID,
        isQuestionApproved: v.isQuestionApproved === true,
      };
      if (!normalizedData[k].questionID) delete normalizedData[k].questionID;
    } else {
      normalizedData[k] = v;
    }
  });
  const questionCount = typeof poll.question_count === "number" ? poll.question_count : poll.question_count != null ? Number(poll.question_count) || Object.keys(normalizedData).length : Object.keys(normalizedData).length || 1;
  const isShuffleNum = poll.is_shuffle === 1 || poll.is_shuffle === true ? 1 : 0;
  const pollType = poll.pollType === "popup" ? "popup" : poll.pollType === "section" ? "section" : undefined;
  const out = {
    isApproved: isApproved === true,
    showresult: poll.showresult === true,
    intropage: poll.intropage === true,
    id: numericId,
    title: typeof poll.title === "string" ? poll.title : typeof poll.question === "string" ? poll.question : "",
    category,
    points: typeof poll.points === "number" ? poll.points : Number(poll.points) || 0,
    duration: poll.duration != null ? String(poll.duration) : "10",
    question_count: questionCount,
    is_shuffle: isShuffleNum,
    data: Object.keys(normalizedData).length ? normalizedData : data,
  };
  if (pollType) out.pollType = pollType;
  // Session start / end poll (Poll tab "Start / End poll") - poll and survey only.
  if (category === "poll" || category === "survey") {
    out.sessionStartPoll = poll.sessionStartPoll === true;
    out.sessionEndPoll = poll.sessionEndPoll === true;
  }
  return out;
}

function reconcileActiveIds(sessionId, kind, activeIds) {
  saveActiveIds(sessionId, kind, new Set([...activeIds].map(String)));
  const idx = sessions.findIndex((s) => s.sessionId === Number(sessionId));
  if (idx === -1) return;
  const session = sessions[idx];
  const ids = loadActiveIds(sessionId, kind);
  if (kind === "qna") {
    const qna = (session.qna || []).map((q) => {
      if (hasRecentLocalToggle(sessionId, "qna", [q.id])) return q;
      return { ...q, isApproved: ids.has(String(q.id)) };
    });
    sessions[idx] = { ...session, qna };
  } else {
    const polls = (session.polls || []).map((p) => {
      if (hasRecentLocalToggle(sessionId, "polls", [p.id])) return p;
      const isActive = p.id != null && ids.has(String(p.id));
      return { ...p, isApproved: isActive };
    });
    sessions[idx] = { ...session, polls };
  }
  notify();
}

export function reconcileActiveQnaIds(sessionId, ids) {
  reconcileActiveIds(sessionId, "qna", ids);
}

export function reconcileActivePoll(sessionId, ids) {
  reconcileActiveIds(sessionId, "polls", ids);
}

export function applyPollHistoryState(sessionId, pollHistory) {
  const idx = sessions.findIndex((s) => s.sessionId === Number(sessionId));
  if (idx === -1 || !Array.isArray(pollHistory) || !pollHistory.length) return;
  const session = sessions[idx];
  const latestById = new Map();
  for (const entry of pollHistory) {
    const msg = entry?.data;
    const ts = typeof entry?.timestamp === "number" ? entry.timestamp : 0;
    if (!msg || typeof msg !== "object") continue;
    const items = Array.isArray(msg.data) ? msg.data : Array.isArray(msg) ? msg : null;
    if (!items) continue;
    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      const pid = item.id;
      if (pid == null) continue;
      const key = String(pid);
      const prev = latestById.get(key);
      if (prev && prev.timestamp > ts) continue;
      if (item.isApproved === true && item.data && typeof item.data === "object") {
        latestById.set(key, { item, timestamp: ts });
      } else if (item.isApproved === false) {
        latestById.set(key, { item, timestamp: ts });
      }
    }
  }
  let changed = false;
  const polls = (session.polls || []).map((p) => {
    const pid = p.id;
    if (pid == null) return p;
    const latest = latestById.get(String(pid))?.item;
    if (!latest) return p;
    // Session start / end poll flags come back with the rest (the backend list never has them).
    const sessionStartPoll = latest.sessionStartPoll === true;
    const sessionEndPoll = latest.sessionEndPoll === true;
    if (latest.isApproved === false) {
      changed = true;
      return { ...p, isApproved: false, showresult: false, intropage: false, sessionStartPoll, sessionEndPoll };
    }
    if (latest.data && typeof latest.data === "object") {
      changed = true;
      const pollType = latest.pollType === "popup" ? "popup" : latest.pollType === "section" ? "section" : p.pollType;
      return { ...p, data: latest.data, isApproved: true, showresult: latest.showresult === true, intropage: latest.intropage === true, question_count: latest.question_count ?? p.question_count, ...(pollType ? { pollType } : {}), sessionStartPoll, sessionEndPoll };
    }
    if (latest.pollType === "popup" || latest.pollType === "section") {
      if (p.pollType !== latest.pollType) {
        changed = true;
        return { ...p, pollType: latest.pollType };
      }
    }
    return p;
  });
  if (changed) {
    sessions[idx] = { ...session, polls };
    notify();
  }
}

let sessions = [];
let listeners = [];
let timer = null;

function notify() {
  clearTimeout(timer);
  timer = setTimeout(() => {
    const snap = [...sessions];
    listeners.forEach((fn) => fn(snap));
  }, 50);
}

export function getSessionById(id) {
  return sessions.find((s) => s.sessionId === Number(id)) || null;
}

const pendingComponentConfig = {};

export function setSessionDetails(id, details) {
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  const existing = idx === -1 ? null : sessions[idx];
  const safePolls = Array.isArray(existing?.polls) ? existing.polls : [];
  const safeQna = Array.isArray(existing?.qna) ? existing.qna : [];
  const next = { ...details, sessionId: Number(id), qna: safeQna, polls: safePolls };

  const pending = pendingComponentConfig[Number(id)];
  if (pending) {
    next.data = { ...(next.data || {}), setting: { ...(next.data?.setting || {}), ...pending } };
    delete pendingComponentConfig[Number(id)];
  }

  // The UI reads !!session.data.streamToUser (Live step / "Go Live") - always expose the
  // key here too, so a seed that never carried it can't leave Live permanently incomplete.
  // endStram (set by Stop Live) likewise: a plain boolean, false unless it says true.
  next.data = {
    ...(next.data || {}),
    streamToUser: next.data?.streamToUser === true,
    endStram: next.data?.endStram === true,
  };

  if (idx === -1) sessions.push(next);
  else sessions[idx] = next;
  notify();
}

export function updateQnaItem(id, question) {
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  if (idx === -1 || !question) return;
  const session = sessions[idx];
  const withApproved = { ...question, isApproved: true };
  const qna = (session.qna || []).map((q) => (String(q.id) === String(question.id) ? { ...q, ...withApproved } : q));
  sessions[idx] = { ...session, qna };
  markIdActive(id, "qna", question.id);
  markRecentLocalToggle(id, "qna", [question.id]);
  notify();
  const payloadItem = buildQnaPayloadItem(withApproved, true);
  pubSub.publish(CHANNELS.QNA, { data: [payloadItem] });
}

export function removeQnaItem(id, questionId) {
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  if (idx === -1) return;
  const session = sessions[idx];
  const existing = (session.qna || []).find((q) => String(q.id) === String(questionId));
  const qna = (session.qna || []).map((q) => (String(q.id) === String(questionId) ? { ...q, isApproved: false } : q));
  sessions[idx] = { ...session, qna };
  markIdInactive(id, "qna", questionId);
  markRecentLocalToggle(id, "qna", [questionId]);
  notify();
  const payloadItem = {
    ...(existing || {}),
    id: existing?.id ?? questionId,
    question: typeof existing?.question === "string" ? existing.question : "",
    isApproved: false,
  };
  pubSub.publish(CHANNELS.QNA, { data: [payloadItem] });
}

export function clearQnaChannel(sessionId) {
  const sid = Number(sessionId);
  const idx = sessions.findIndex((s) => s.sessionId === sid);
  if (idx === -1) return;
  const session = sessions[idx];
  const channelName = `streaming:${CHANNELS.QNA}-${sid}`;
  const activeQuestions = (session.qna || []).filter((q) => q.isApproved);

  saveActiveIds(sid, "qna", new Set());

  const qna = (session.qna || []).map((q) => (q.isApproved ? { ...q, isApproved: false } : q));
  sessions[idx] = { ...session, qna };
  markRecentLocalToggle(sid, "qna", activeQuestions.map((q) => q.id));
  notify();

  console.log(`[adminStore][ABLY PUB CLEAR] ${channelName} deep clean ${activeQuestions.length} questions`);

  for (const q of activeQuestions) {
    const payloadItem = { ...q, id: q.id, question: typeof q.question === "string" ? q.question : "", isApproved: false };
    pubSub.publish(CHANNELS.QNA, { data: [payloadItem] });
  }

  const pub = pubSub.publish(CHANNELS.QNA, { data: [] });
  if (pub && typeof pub.catch === "function") {
    pub.catch((e) => console.error(`[adminStore] publish clear ${channelName} failed:`, e?.message || e));
  }
}

export function setAdminQna(id, qna) {
  const safeQna = Array.isArray(qna) ? qna : [];
  const activeIds = loadActiveIds(id, "qna");
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  const withActive = safeQna.map((q) => ({
    ...q,
    ...(activeIds.has(String(q.id)) ? { isApproved: true } : {}),
  }));
  if (idx === -1) sessions.push({ sessionId: Number(id), qna: withActive, polls: [] });
  else sessions[idx] = { ...sessions[idx], qna: withActive };
  notify();
}

export function updatePollItem(id, poll) {
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  if (idx === -1 || !poll || poll.id == null) {
    console.warn("[adminStore] updatePollItem: missing session or poll", { id, poll });
    return;
  }
  const session = sessions[idx];
  const numericId = poll.id;
  const withApproved = { ...poll, id: numericId, isApproved: true };

  let polls = (session.polls || []).map((p) => {
    if (String(p.id) === String(numericId)) return { ...p, ...withApproved };
    return p;
  });
  const exists = polls.some((p) => String(p.id) === String(numericId));
  if (!exists) {
    polls = [...polls, withApproved];
  }
  sessions[idx] = { ...session, polls };
  markIdActive(id, "polls", numericId);
  markRecentLocalToggle(id, "polls", [numericId]);
  notify();
  const channelName = `streaming:${CHANNELS.POLL}-${id}`;

  if (isInteractiveQuiz(withApproved)) {
    const payloadItem = buildPollPayloadItem(withApproved, true);
    console.log(`[adminStore][ABLY PUB] ${channelName}`, JSON.stringify(payloadItem).slice(0, 800));
    const pub = pubSub.publish(CHANNELS.POLL, { data: [payloadItem] });
    if (pub && typeof pub.catch === "function") {
      pub.catch((e) => console.error(`[adminStore] publish ${channelName} failed:`, e?.message || e));
    }
    try {
      const qKeys = Object.keys(payloadItem.data || {}).filter((k) => /^question\d+$/i.test(k));
      const approvedKeys = qKeys.filter((k) => payloadItem.data[k]?.isQuestionApproved === true).map((k) => `${k}:${payloadItem.data[k]?.questionID}`);
      console.log(`[adminStore] id=${payloadItem.id} isApproved=${payloadItem.isApproved} approvedQuestions=${approvedKeys.join(",") || "none (intro)"}`);
    } catch { /* debug logging is best-effort */ }
    return;
  }

  const pollPayload = buildPollPayloadItem({ ...withApproved, isApproved: true }, true);
  pollPayload.isApproved = true;
  pollPayload.showresult = withApproved.showresult === true;
  delete pollPayload.intropage;
  console.log(`[adminStore][ABLY PUB] ${channelName} poll`, JSON.stringify(pollPayload).slice(0, 800));
  const pub = pubSub.publish(CHANNELS.POLL, { data: [pollPayload] });
  if (pub && typeof pub.catch === "function") {
    pub.catch((e) => console.error(`[adminStore] publish ${channelName} failed:`, e?.message || e));
  }
}

export function removePollItem(id, itemId) {
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  if (idx === -1) return;
  const session = sessions[idx];
  const poll = (session.polls || []).find((p) => String(p.id) === String(itemId));
  const numericId = poll?.id ?? itemId;
  const polls = (session.polls || []).map((p) => {
    return String(p.id) === String(numericId) ? { ...p, isApproved: false, showresult: false, intropage: false } : p;
  });
  sessions[idx] = { ...session, polls };
  markIdInactive(id, "polls", numericId);
  markRecentLocalToggle(id, "polls", [numericId]);
  notify();

  const interactive = poll ? isInteractiveQuiz(poll) : false;
  if (interactive) {
    const payloadBase = poll ? { ...poll, isApproved: false, showresult: false, intropage: false } : { isApproved: false, showresult: false, intropage: false, id: numericId, title: "", category: "poll", points: 0, duration: "10", question_count: 1, is_shuffle: 0, data: { question1: { question: "", type: "single", options: [] } } };
    if (payloadBase.id == null) payloadBase.id = numericId;
    const payloadItem = buildPollPayloadItem(payloadBase, false);
    pubSub.publish(CHANNELS.POLL, { data: [payloadItem] });
    const ts = Math.floor(Date.now() / 1000);
    pubSub.publish(CHANNELS.QUIZ_RESULT, { data: [], pollId: numericId, isActive: false, timestamp: ts }).catch(()=>{});
    return;
  }

  const base = poll ? { ...poll, isApproved: false } : { isApproved: false, id: numericId, title: "", category: "poll", points: 0, duration: "10", question_count: 1, is_shuffle: 0, data: { question1: { question: "", type: "single", options: [] } } };
  if (base.id == null) base.id = numericId;
  base.isApproved = false;
  const item = buildPollPayloadItem(base, false);
  item.isApproved = false;
  item.showresult = false;
  delete item.intropage;
  pubSub.publish(CHANNELS.POLL, { data: [item] });
}

export function clearPollChannel(sessionId) {
  const sid = Number(sessionId);
  const idx = sessions.findIndex((s) => s.sessionId === sid);
  if (idx === -1) return;
  const session = sessions[idx];
  const channelName = `streaming:${CHANNELS.POLL}-${sid}`;
  const isTargetPoll = (p) => {
    if (isInteractiveQuiz(p)) return false;
    const cat = typeof p.category === "string" ? p.category.trim().toLowerCase() : "";
    const type = typeof p.type === "string" ? p.type.trim() : "";
    if (cat === "poll" || cat === "survey") return true;
    if (type === "poll" || type === "survey" || type === "SurveyJs") return true;
    return p.data && typeof p.data === "object" && !Array.isArray(p.data);
  };
  const targetPolls = (session.polls || []).filter(isTargetPoll);
  const activeTargetPolls = targetPolls.filter((p) => p.isApproved);
  const activeIds = loadActiveIds(sid, "polls");
  for (const p of targetPolls) {
    activeIds.delete(String(p.id));
  }
  saveActiveIds(sid, "polls", activeIds);
  const polls = (session.polls || []).map((p) => (isTargetPoll(p) ? { ...p, isApproved: false, showresult: false } : p));
  sessions[idx] = { ...session, polls };
  markRecentLocalToggle(sid, "polls", activeTargetPolls.map((p) => p.id));
  notify();
  console.log(`[adminStore][ABLY PUB CLEAR] ${channelName} deep clean ${activeTargetPolls.length} target polls`);
  for (const p of activeTargetPolls) {
    pubSub.publish(CHANNELS.POLL, { id: p.id }, "poll-inactive");
  }
}

export function patchPollLocal(sessionId, id, patch) {
  const idx = sessions.findIndex((s) => s.sessionId === Number(sessionId));
  if (idx === -1 || !patch || typeof patch !== "object") return;
  const session = sessions[idx];
  const targetId = String(id);
  let changed = false;
  const polls = (session.polls || []).map((p) => {
    if (String(p.id) === targetId) {
      changed = true;
      return { ...p, ...patch };
    }
    return p;
  });
  if (changed) {
    sessions[idx] = { ...session, polls };
    notify();
  }
}

export function setAdminPoll(id, polls) {
  const safePolls = Array.isArray(polls) ? polls : [];
  const activeIds = loadActiveIds(id, "polls");
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  const existingPolls = idx === -1 ? [] : sessions[idx].polls || [];
  const findExisting = (numeric) =>
    existingPolls.find((ep) => numeric != null && String(ep.id) === String(numeric));
  const withActive = safePolls.map((p) => {
    const numeric = p.id;
    const isActive = numeric != null && activeIds.has(String(numeric));
    const existing = findExisting(numeric);
    const priorPollType = p.pollType || existing?.pollType;
    const priorShowresult = existing?.showresult === true;
    const priorIntropage = existing?.intropage === true;
    const priorStartPoll = existing?.sessionStartPoll === true;
    const priorEndPoll = existing?.sessionEndPoll === true;
    return {
      ...p,
      ...(isActive ? { isApproved: true } : {}),
      ...(priorPollType ? { pollType: priorPollType } : {}),
      ...(priorShowresult ? { showresult: true } : {}),
      ...(priorIntropage ? { intropage: true } : {}),
      ...(priorStartPoll ? { sessionStartPoll: true } : {}),
      ...(priorEndPoll ? { sessionEndPoll: true } : {}),
    };
  });
  if (idx === -1) {
    sessions.push({ sessionId: Number(id), qna: [], polls: withActive });
    notify();
    return;
  }
  const incomingIds = new Set(withActive.map((p) => String(p.id)));
  const keptQuizzes = existingPolls.filter((p) => isInteractiveQuiz(p) && !incomingIds.has(String(p.id)));
  sessions[idx] = { ...sessions[idx], polls: [...withActive, ...keptQuizzes] };
  notify();
}

export function updateQuizFlags(id, partial) {
  const session = sessions.find((s) => s.sessionId === Number(id));
  if (!session) return;
  let changed = false;
  if (typeof partial.interactiveQuizEnabled === "boolean") {
    session.interactiveQuizEnabled = partial.interactiveQuizEnabled;
    changed = true;
  }
  if ("currentInteractiveQuizId" in partial) {
    session.currentInteractiveQuizId = partial.currentInteractiveQuizId;
    changed = true;
  }
  if (!changed) return;
  notify();
}

export function restoreComponentConfig(id, config) {
  if (!id || !config || typeof config !== "object") return;
  const session = sessions.find((s) => s.sessionId === Number(id));
  if (session) {
    const idx = sessions.indexOf(session);
    sessions[idx] = { ...session, data: { ...session.data, setting: { ...session.data?.setting, ...config } } };
    notify();
  } else {
    pendingComponentConfig[Number(id)] = config;
  }
}

export function updateComponentConfig(id, config) {
  if (!id || !config || typeof config !== "object") return;
  const numId = Number(id);
  const session = sessions.find((s) => s.sessionId === numId);
  if (!session) {
    pendingComponentConfig[numId] = config;
    suppressSettingEcho();
    pubSub.publish(CHANNELS.SESSION_DETAILS, { data: { setting: config } }).catch((e) => console.warn("[adminStore] publish setting failed:", e?.message || e));
    return;
  }
  const idx = sessions.indexOf(session);
  sessions[idx] = { ...session, data: { ...session.data, setting: { ...session.data?.setting, ...config } } };
  notify();
  suppressSettingEcho();
  pubSub.publish(CHANNELS.SESSION_DETAILS, { data: { setting: config } }).catch((e) => console.warn("[adminStore] publish setting failed:", e?.message || e));
}

// Ask every open viewer tab to reload: publish a typed control command on cme-common as
// a *named* Ably message, so it is never mistaken for session data. The viewer
// (cme-core useSessionUpdates) honours it live only, keyed on commandId, and rate-limits
// reloads - one click, one reload per tab, no way to loop.
export function publishReloadToken(id) {
  const numId = Number(id);
  if (!numId) return Promise.reject(new Error("missing session id"));
  const command = {
    v: 1,
    kind: "reload",
    commandId: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    sessionId: String(numId),
    issuedAt: Date.now(),
  };
  // Wrapped so a synchronous "no channel" throw still comes back as a rejection, and
  // raced with a timeout so the button can't sit on "Sending…" while the socket is down.
  let timer = null;
  return Promise.race([
    Promise.resolve().then(() =>
      pubSub.publish(CHANNELS.SESSION_DETAILS, command, MESSAGE_NAMES.CONTROL),
    ),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error("publish timed out")), 8000);
    }),
  ]).finally(() => clearTimeout(timer));
}

// Go Live / Stop Live. The Ably publish is what viewers act on, so it always goes out -
// even when this tab's store has no entry for the session yet (it used to return early
// there, which made Stop Live a silent no-op).
// The cme-common query cache is updated too, not just the store: useCmeSessionSync
// re-mirrors that cache into the store on every channel message (health ticks every
// 1-2s), so a store-only write was reverted to the old value before our own publish
// echoed back - the button flipped back to "Stop Live" as if the click had failed.
// endStram travels with it, always the opposite: Go Live = { streamToUser: true,
// endStram: false }, Stop Live = { streamToUser: false, endStram: true }. Viewers show the
// "live stream ended" screen on endStram (not the waiting room, which is for a stream that
// never started), and Go Live again brings them back. One message carries both, so a
// viewer can never see one flag flipped without the other.
export function toggleStreamToUser(id, streamToUser) {
  const value = Boolean(streamToUser);
  const flags = { streamToUser: value, endStram: !value };
  queryClient.setQueryData(buildSessionCacheKey(id), (old) =>
    old ? { ...old, data: { ...old.data, ...flags } } : old,
  );
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  if (idx !== -1) {
    sessions[idx] = { ...sessions[idx], data: { ...sessions[idx].data, ...flags } };
    notify();
  }
  pubSub.publish(CHANNELS.SESSION_DETAILS, { data: flags });
}

// Like / Share on the viewer page (Settings modal): cme-common's detail.isLikeEnabled /
// detail.isShareEnabled. Published as a detail update - { data: { detail: flags } } - which
// both apps merge into detail key by key, so the title, speakers, ... stay as they are.
// Written to the query cache and the store first, for the same re-mirror reason as
// toggleStreamToUser. Returns the publish (rejects when it fails).
export function updateDetailFlags(id, flags) {
  if (!id || !flags || typeof flags !== "object") return Promise.resolve();
  const withFlags = (data) => ({ ...data, detail: { ...(data?.detail || {}), ...flags } });
  queryClient.setQueryData(buildSessionCacheKey(id), (old) =>
    old ? { ...old, data: withFlags(old.data) } : old,
  );
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  if (idx !== -1) {
    sessions[idx] = { ...sessions[idx], data: withFlags(sessions[idx].data) };
    notify();
  }
  return pubSub.publish(CHANNELS.SESSION_DETAILS, { data: { detail: flags } });
}

// Local-only streaming.vendor write after Generate Streaming picked the hosting vendor
// itself (zoom+zoom, ...). The Streaming steps are drawn from streaming.vendor, and
// until CME's own cme-common push lands they kept showing the old vendor's
// "Stream Link" / "Zoom Stream" steps (only a reload fixed it). Written to the query
// cache as well as the store, for the same re-mirror reason as toggleStreamToUser.
// Nothing is published: CME owns this field and pushes the real value itself.
export function setStreamingVendorLocal(id, vendor) {
  const withVendor = (data) => ({ ...data, streaming: { ...(data?.streaming || {}), vendor } });
  queryClient.setQueryData(buildSessionCacheKey(id), (old) =>
    old ? { ...old, data: withVendor(old.data) } : old,
  );
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  if (idx !== -1) {
    sessions[idx] = { ...sessions[idx], data: withVendor(sessions[idx].data) };
    notify();
  }
}

export function subscribe(fn) {
  listeners.push(fn);
  fn([...sessions]);
  return () => {
    listeners = listeners.filter((f) => f !== fn);
  };
}

export function updateUserMeetingAccess(id, userId, { admitted, vendorKey, role } = {}) {
  const idx = sessions.findIndex((s) => s.sessionId === Number(id));
  if (idx === -1 || !userId) return;
  const session = sessions[idx];
  const admittedUsers = { ...session.admittedUsers };
  if (admitted) {
    admittedUsers[userId] = { vendorKey, role: role || admittedUsers[userId]?.role || "hcp" };
  } else {
    delete admittedUsers[userId];
  }
  sessions[idx] = { ...session, admittedUsers };
  notify();
}
